import { useMemo } from 'react';
import {
  Table,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { FieldSection } from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { buildUnitHeaderOptions, unitTypeLabel } from '../constants/declarationUnits';
import { buildMassBalancePeriodColumns } from '../constants/massBalancePeriodColumns';
import styles from './DeclarationOverviewTabView.module.scss';

// TODO: add the info icons beside the quantity labels and headers once a Tooltip ships.

// The card's columns: declaration ▸ working ▸ tracking, then the attributes.
const UNIT_COLUMN_ORDER = Object.freeze([
  UNIT_TYPES.declaration,
  UNIT_TYPES.working,
  UNIT_TYPES.tracking,
]);

const getPeriodRowId = (row) => row.id;

/**
 * Props:
 *   declaration  the declaration detail view model
 */
export const DeclarationOverviewTabView = ({ declaration }) => {
  const t = useTranslations();

  const periodRows = declaration.massBalancePeriodRows;

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(
    periodRows,
    buildUnitHeaderOptions,
    t,
  );

  const columns = useMemo(
    () => buildMassBalancePeriodColumns(t, { unitHeaderSelect }),
    [t, unitHeaderSelect],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  // All three columns always show; a leg the server could not resolve dashes out.
  // TODO: show the server's reason once DeclarationQuantities carries one, as Contract does.
  const quantitySections = useMemo(
    () =>
      UNIT_COLUMN_ORDER.map((unitType) => {
        const view = declaration.byUnit[unitType];
        return {
          unitType,
          heading: view?.unit
            ? t('SECTION_QUANTITY_IN_UNIT', { unit: view.unit })
            : t('SECTION_QUANTITY_IN_UNIT_TYPE', { unitType: unitTypeLabel(t, unitType) }),
          fields: [
            { label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'), value: view?.receivedQty ?? '-' },
            { label: t('DECLARATIONS_COL_SURPLUS_QTY'), value: view?.surplusQty ?? '-' },
            { label: t('DECLARATIONS_COL_WRITE_OFF_QTY'), value: view?.writeOffQty ?? '-' },
            { label: t('DECLARATIONS_COL_ADJUSTED_QTY'), value: view?.adjustedQty ?? '-' },
            { label: t('FIELD_AVAILABLE_QUANTITY'), value: view?.availableQty ?? '-' },
          ],
        };
      }),
    [t, declaration.byUnit],
  );

  const sustainabilityFields = useMemo(
    () => [
      { label: t('FIELD_GHG_EMISSIONS'), value: declaration.ghgEmissions },
      { label: t('FIELD_GHG_EMISSION_SAVINGS'), value: declaration.ghgEmissionSavings },
      { label: t('FIELD_RAW_MATERIAL_TYPE'), value: declaration.rawMaterialType },
      { label: t('FIELD_COUNTRY_OF_ORIGIN'), value: declaration.countryOfOrigin },
      { label: t('FIELD_FEEDSTOCK_CATEGORY'), value: declaration.feedstockCategory },
      { label: t('FIELD_ALLOCATION_PREFERENCE'), value: declaration.allocationPreference },
    ],
    [t, declaration],
  );

  return (
    <div className={styles.tab}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Typography
            variant={TypographyVariants.bodySm}
            weight={TypographyWeights.semibold}
            color={TypographyColors.primary}
          >
            {t('DECLARATIONS_DETAIL_CARD_TITLE')}
          </Typography>
        </div>
        <div className={styles.cardBody}>
          {quantitySections.map((section) => (
            <FieldSection
              key={section.unitType}
              heading={section.heading}
              fields={section.fields}
            />
          ))}
          <FieldSection heading={t('SECTION_SUSTAINABILITY')} fields={sustainabilityFields} />
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Typography
            variant={TypographyVariants.bodySm}
            weight={TypographyWeights.semibold}
            color={TypographyColors.primary}
          >
            {t('DECLARATIONS_DETAIL_MASS_BALANCE_TITLE')}
          </Typography>
        </div>
        <UnitTypeContext.Provider value={contextValue}>
          <Table
            columns={columns}
            data={periodRows}
            getRowId={getPeriodRowId}
            labels={tableLabels}
          />
        </UnitTypeContext.Provider>
      </div>
    </div>
  );
};
